// Pure aggregations for the Customer Actions tabs (tested in __tests__/helpers.test.js).

export const TABS = [
  { id: "actions", label: "Finding actions", hint: "what users do with findings" },
  { id: "emails", label: "Emails", hint: "run-complete notifications" },
  { id: "announcements", label: "Announcements", hint: "the in-app what's-new dialog" },
  { id: "journeys", label: "Journeys", hint: "funnel, entry paths, surfaces, Activity feed" },
  { id: "feed", label: "Raw feed", hint: "every captured event" },
];
export const DEFAULT_TAB = "actions";
export const TIME_RANGES = ["7d", "30d", "90d", "1y"];

export function parseTab(value) {
  return TABS.some((t) => t.id === value) ? value : DEFAULT_TAB;
}

const RANGE_DAYS = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 };

// UTC window for a page range preset (ISO strings, end = now).
export function rangeToIso(range, now = new Date()) {
  const days = RANGE_DAYS[range] || 30;
  return { start: new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString(), end: now.toISOString() };
}

export function parseRange(value) {
  return TIME_RANGES.includes(value) ? value : "30d";
}

const pctOf = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

// ---- Finding actions ----
export function lifecycleMix(stats) {
  const shares = stats && stats.share_targets ? Object.values(stats.share_targets).reduce((s, v) => s + v, 0) : 0;
  const data = [
    { name: "Marks done", value: (stats && stats.marks_done) || 0, color: "#1FAF5C" },
    { name: "Discards", value: (stats && stats.discards) || 0, color: "#FF7B43" },
    { name: "Claims (I'm on it)", value: (stats && stats.assignments) || 0, color: "#8B5CF6" },
    { name: "Drag-reorders", value: (stats && stats.drag_reorders) || 0, color: "#EC4899" },
    { name: "Shares", value: shares, color: "#6C7187" },
  ];
  const total = data.reduce((s, i) => s + i.value, 0);
  return { data: data.map((i) => ({ ...i, percentage: pctOf(i.value, total) })), total };
}

// Real click counts per ranked position (the old page scaled these into invented rates).
export function actionsByPosition(stats) {
  if (!stats || !stats.action_by_position) return [];
  return Object.entries(stats.action_by_position)
    .map(([pos, count]) => ({ position: parseInt(pos, 10), count }))
    .filter((r) => !Number.isNaN(r.position))
    .sort((a, b) => a.position - b.position);
}

export function discardReasons(stats) {
  if (!stats || !stats.discard_reasons) return [];
  const raw = Object.entries(stats.discard_reasons);
  const total = raw.reduce((s, r) => s + r[1], 0);
  return raw.sort((a, b) => b[1] - a[1]).map(([reason, count]) => ({ reason, count, percentage: pctOf(count, total) }));
}

export function shareTargets(stats) {
  if (!stats || !stats.share_targets) return [];
  return Object.entries(stats.share_targets)
    .sort((a, b) => b[1] - a[1])
    .map(([target, count]) => ({ target, count }));
}

// ---- Emails ----
export function splitNotifications(source) {
  const rows = Array.isArray(source) ? source.filter((e) => !e.dry_run) : [];
  return { sends: rows.filter((e) => e.sent_at), skips: rows.filter((e) => !e.sent_at) };
}

export function notificationTotals(sends) {
  let deliveries = 0;
  let issueBearing = 0;
  let allClear = 0;
  const runs = new Set();
  const recipients = new Set();
  sends.forEach((e) => {
    deliveries += (e.recipients || []).length;
    (e.recipients || []).forEach((r) => recipients.add((r || "").toLowerCase()));
    if (e.state === "issue_bearing") issueBearing += 1;
    else if (e.state === "all_clear") allClear += 1;
    if (e.run_id) runs.add(e.run_id);
  });
  return { sends: sends.length, deliveries, issueBearing, allClear, uniqueRuns: runs.size, uniqueRecipients: recipients.size };
}

const dayKey = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function notificationsOverTime(sends) {
  const byDay = new Map();
  sends.forEach((e) => {
    const d = new Date(e.sent_at);
    const key = dayKey(d);
    if (!byDay.has(key)) byDay.set(key, { date: key, ts: new Date(d.toDateString()).getTime(), "Issue-bearing": 0, "All clear": 0 });
    const bucket = byDay.get(key);
    if (e.state === "all_clear") bucket["All clear"] += 1;
    else bucket["Issue-bearing"] += 1;
  });
  return Array.from(byDay.values()).sort((a, b) => a.ts - b.ts);
}

const FIRE_PATH_COLORS = { settle: "#8B5CF6", console_manual: "#1FAF5C", console_push: "#FF7B43", mcp: "#EC4899" };

export function firePathMix(sends) {
  const counts = new Map();
  sends.forEach((e) => {
    const key = e.fire_path || "legacy";
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value, color: FIRE_PATH_COLORS[name] || "#6C7187", percentage: pctOf(value, sends.length) }));
}

// ---- Announcements ----
export function announcementEventsOf(source) {
  return (Array.isArray(source) ? source : []).filter((e) => e.event_type && e.event_type.startsWith("release_announcement."));
}

export function announcementSummary(events) {
  const counts = { viewed: 0, dismissed: 0, blog_clicked: 0, video_played: 0 };
  const dismissByMethod = { cta: 0, close_button: 0, outside_or_esc: 0 };
  let openDurationSum = 0;
  let openDurationN = 0;
  let reopened = 0;
  events.forEach((e) => {
    const props = e.properties || {};
    switch (e.event_type) {
      case "release_announcement.viewed":
        counts.viewed += 1;
        if (props.reopened) reopened += 1;
        break;
      case "release_announcement.dismissed":
        counts.dismissed += 1;
        if (props.method && dismissByMethod[props.method] !== undefined) dismissByMethod[props.method] += 1;
        if (props.open_duration_ms != null) {
          openDurationSum += props.open_duration_ms;
          openDurationN += 1;
        }
        break;
      case "release_announcement.blog_clicked":
        counts.blog_clicked += 1;
        break;
      case "release_announcement.video_played":
        counts.video_played += 1;
        break;
      default:
        break;
    }
  });
  const dismissData = [
    { name: "CTA button", value: dismissByMethod.cta, color: "#1FAF5C" },
    { name: "Close (X)", value: dismissByMethod.close_button, color: "#FF7B43" },
    { name: "Outside / Esc", value: dismissByMethod.outside_or_esc, color: "#6C7187" },
  ].map((i) => ({ ...i, percentage: pctOf(i.value, counts.dismissed) }));
  return {
    counts,
    dismissData,
    blogCtr: pctOf(counts.blog_clicked, counts.viewed),
    videoCtr: pctOf(counts.video_played, counts.viewed),
    reopenRate: pctOf(reopened, counts.viewed),
    avgOpenDurationMs: openDurationN > 0 ? openDurationSum / openDurationN : 0,
  };
}

export function announcementOverTime(events) {
  const byDay = new Map();
  events.forEach((e) => {
    if (!e.timestamp) return;
    if (e.event_type !== "release_announcement.viewed" && e.event_type !== "release_announcement.dismissed") return;
    const d = new Date(e.timestamp);
    const key = dayKey(d);
    if (!byDay.has(key)) byDay.set(key, { date: key, ts: d.getTime(), Views: 0, Dismissals: 0 });
    const bucket = byDay.get(key);
    if (e.event_type === "release_announcement.viewed") bucket.Views += 1;
    else bucket.Dismissals += 1;
  });
  return Array.from(byDay.values()).sort((a, b) => a.ts - b.ts);
}

export function announcementByRelease(events) {
  const byVersion = new Map();
  events.forEach((e) => {
    const version = (e.properties && e.properties.release_version) || "unknown";
    if (!byVersion.has(version)) {
      byVersion.set(version, { version, views: 0, dismissals: 0, blogClicks: 0, videoPlays: 0, openDurationSum: 0, openDurationN: 0 });
    }
    const row = byVersion.get(version);
    switch (e.event_type) {
      case "release_announcement.viewed":
        row.views += 1;
        break;
      case "release_announcement.dismissed":
        row.dismissals += 1;
        if (e.properties && e.properties.open_duration_ms != null) {
          row.openDurationSum += e.properties.open_duration_ms;
          row.openDurationN += 1;
        }
        break;
      case "release_announcement.blog_clicked":
        row.blogClicks += 1;
        break;
      case "release_announcement.video_played":
        row.videoPlays += 1;
        break;
      default:
        break;
    }
  });
  return Array.from(byVersion.values())
    .map((r) => ({ ...r, blogCtr: pctOf(r.blogClicks, r.views), avgOpenMs: r.openDurationN > 0 ? r.openDurationSum / r.openDurationN : 0 }))
    .sort((a, b) => b.views - a.views);
}

// ---- Raw feed ----
export function uniqueEventTypes(events) {
  return Array.from(new Set((events || []).map((e) => e.event_type).filter(Boolean))).sort();
}

export function uniqueUsers(events) {
  const usersMap = new Map();
  (events || []).forEach((e) => {
    if (e.user_email) usersMap.set(e.user_id, e.user_email);
    else if (!usersMap.has(e.user_id)) usersMap.set(e.user_id, e.user_id);
  });
  return Array.from(usersMap.entries()).map(([id, label]) => ({ id, label }));
}

export function filterEvents(events, { type = "all", user = "all", website = "all", search = "" } = {}) {
  const term = (search || "").toLowerCase();
  return (events || []).filter((e) => {
    if (type !== "all" && e.event_type !== type) return false;
    if (user !== "all" && e.user_id !== user) return false;
    if (website !== "all") {
      const eventWebsiteId = e.website_id || (e.properties && (e.properties.website_id || e.properties.website));
      if (eventWebsiteId !== website && !JSON.stringify(e.properties || {}).includes(website)) return false;
    }
    if (!term) return true;
    return [e.user_id, e.user_email, e.session_id, e.event_type, e.finding_id, e.group_id, e.scan_id]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(term));
  });
}
